import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box, makeStyles } from '@material-ui/core';
import clsx from 'clsx';

import { Close } from '~/views/private/overview/ContentEditor/icons/Close';

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: '144px 80px',
    background: 'var(--surface-tint-strong, #171C2633)',
  },
  container: {
    boxSizing: 'border-box',
    width: '100%',
    height: 'auto',
    maxHeight: '100%',
    minWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    background: 'var(--surface-default, #FFFFFF)',
    boxShadow: 'var(--shadows-modal)',
    overflow: 'hidden',
    outline: 'none',
  },
  header: {
    boxSizing: 'border-box',
    flexShrink: 0,
    height: 'var(--utilities-size-3-500, 56px)',
    padding: '0 var(--utilities-size-0-750, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--utilities-size-0-500, 8px)',
  },
  title: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'var(--content-primary)',
    fontFamily: 'var(--typography-font-family-default) !important',
    fontSize: 'var(--typography-font-size-lg)',
    fontWeight: 700,
    lineHeight: 'var(--typography-line-height-lg-line)',
  },
  closeButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--content-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    boxSizing: 'border-box',
    position: 'relative',
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bodySingle: {
    paddingBottom: 'var(--utilities-size-3-500, 56px)',
  },
  media: {
    display: 'block',
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 400px)',
    objectFit: 'contain',
    userSelect: 'none',
  },
  mediaWithControls: {
    maxHeight: 'calc(100vh - 409px)',
  },
  controls: {
    boxSizing: 'border-box',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--utilities-size-1-500, 24px)',
    width: '100%',
    paddingTop: 'var(--utilities-size-1-000, 16px)',
    paddingBottom: 'var(--utilities-size-1-000, 16px)',
    paddingLeft: 'var(--utilities-size-1-000, 16px)',
    paddingRight: 'var(--utilities-size-1-000, 16px)',
    borderTop: '1px solid var(--border-default, rgba(23, 28, 38, 0.10))',
  },
  navigation: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--utilities-size-0-250, 4px)',
    flexShrink: 0,
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 'var(--utilities-size-0-250, 4px)',
    border: 'none',
    background: 'transparent',
    borderRadius: 'var(--utilities-border-radius-default, 8px)',
    color: 'var(--content-secondary, #6b7280)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--surface-hover-default-hover, #F5F6FF)',
      color: 'var(--content-hover-secondary-hover, #5646e5)',
    },
    '&:focus-visible': {
      outline: '2px solid var(--border-accent, #5646e5)',
      outlineOffset: 2,
    },
  },
  navIcon: {
    display: 'block',
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    height: 14,
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  bullet: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    flexShrink: 0,
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: '2px solid var(--border-accent, #5646e5)',
      outlineOffset: 1,
    },
  },
  bulletDot: {
    display: 'block',
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--surface-tint-medium, #171C261A)',
  },
  bulletDotActive: {
    background: 'var(--surface-inverted, #2C313A)',
  },
}));

function PreviewChevron({ direction, className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d={
          direction === 'left'
            ? 'M15.5312 18.9694C15.6009 19.039 15.6562 19.1218 15.6939 19.2128C15.7316 19.3039 15.751 19.4014 15.751 19.5C15.751 19.5985 15.7316 19.6961 15.6939 19.7872C15.6562 19.8782 15.6009 19.9609 15.5312 20.0306C15.4615 20.1003 15.3788 20.1556 15.2878 20.1933C15.1967 20.231 15.0991 20.2504 15.0006 20.2504C14.902 20.2504 14.8045 20.231 14.7134 20.1933C14.6224 20.1556 14.5396 20.1003 14.47 20.0306L6.96996 12.5306C6.90023 12.461 6.84491 12.3782 6.80717 12.2872C6.76943 12.1961 6.75 12.0986 6.75 12C6.75 11.9014 6.76943 11.8038 6.80717 11.7128C6.84491 11.6217 6.90023 11.539 6.96996 11.4694L14.47 3.96936C14.6107 3.82863 14.8016 3.74957 15.0006 3.74957C15.1996 3.74957 15.3905 3.82863 15.5312 3.96936C15.6719 4.1101 15.751 4.30097 15.751 4.49999C15.751 4.69901 15.6719 4.88988 15.5312 5.03061L8.5609 12L15.5312 18.9694Z'
            : 'M17.031 12.5306L9.53104 20.0306C9.46136 20.1003 9.37863 20.1556 9.28759 20.1933C9.19654 20.231 9.09896 20.2504 9.00042 20.2504C8.90187 20.2504 8.80429 20.231 8.71324 20.1933C8.6222 20.1556 8.53947 20.1003 8.46979 20.0306C8.40011 19.9609 8.34483 19.8782 8.30712 19.7872C8.26941 19.6961 8.25 19.5985 8.25 19.5C8.25 19.4014 8.26941 19.3039 8.30712 19.2128C8.34483 19.1218 8.40011 19.039 8.46979 18.9694L15.4401 12L8.46979 5.03061C8.32906 4.88988 8.25 4.69901 8.25 4.49999C8.25 4.30097 8.32906 4.1101 8.46979 3.96936C8.61052 3.82863 8.80139 3.74957 9.00042 3.74957C9.19944 3.74957 9.39031 3.82863 9.53104 3.96936L17.031 11.4694C17.1008 11.539 17.1561 11.6217 17.1938 11.7128C17.2316 11.8038 17.251 11.9014 17.251 12C17.251 12.0986 17.2316 12.1961 17.1938 12.2872C17.1561 12.3782 17.1008 12.461 17.031 12.5306Z'
        }
        fill="currentColor"
      />
    </svg>
  );
}

PreviewChevron.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  className: PropTypes.string,
};

PreviewChevron.defaultProps = {
  className: undefined,
};

function previewFileNameFromUrl(url) {
  const path = String(url || '').split('?')[0];
  return path.split('/').pop() || 'Content';
}

export const PreviewMediaModal = ({
  name,
  url,
  showVideo,
  fileIndex,
  fileCount,
  onFileIndexChange,
  onClose,
}) => {
  const classes = useStyles();
  const displayName = (name && name.trim()) || previewFileNameFromUrl(url);
  const canSwitch = fileCount > 1;
  const dialogRef = useRef(null);
  const activeBulletRef = useRef(null);
  const lastWheelAtRef = useRef(0);

  const goToOffset = useCallback(
    delta => {
      if (fileCount <= 1) return;
      const next = (fileIndex + delta + fileCount) % fileCount;
      if (next !== fileIndex) onFileIndexChange(next);
    },
    [fileCount, fileIndex, onFileIndexChange],
  );

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
  }, []);

  useEffect(() => {
    if (activeBulletRef.current) {
      activeBulletRef.current.scrollIntoView({
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [fileIndex]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (!canSwitch) return;
      const { target } = event;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToOffset(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToOffset(1);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [canSwitch, goToOffset, onClose]);

  useEffect(() => {
    const node = dialogRef.current;
    if (!node || !canSwitch) return undefined;
    const onWheel = event => {
      event.preventDefault();
      const dominant =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (Math.abs(dominant) < 8) return;
      const now = Date.now();
      if (now - lastWheelAtRef.current < 350) return;
      lastWheelAtRef.current = now;
      goToOffset(dominant > 0 ? 1 : -1);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [canSwitch, goToOffset]);

  return createPortal(
    <Box
      className={classes.overlay}
      onClick={onClose}
      onMouseDown={event => event.stopPropagation()}
    >
      <Box
        ref={dialogRef}
        className={classes.container}
        role="dialog"
        aria-modal="true"
        aria-label={
          canSwitch
            ? `Preview ${displayName}, file ${fileIndex + 1} of ${fileCount}`
            : `Preview ${displayName}`
        }
        tabIndex={-1}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
      >
        <Box className={classes.header}>
          <Box className={classes.title}>{displayName}</Box>
          <button
            type="button"
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close preview"
          >
            <Close color="var(--content-primary)" />
          </button>
        </Box>
        <Box className={clsx(classes.body, !canSwitch && classes.bodySingle)}>
          {showVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={url}
              src={url}
              className={clsx(
                classes.media,
                canSwitch && classes.mediaWithControls,
              )}
              controls
              playsInline
            />
          ) : (
            <img
              key={url}
              src={url}
              alt={displayName}
              className={clsx(
                classes.media,
                canSwitch && classes.mediaWithControls,
              )}
              draggable={false}
            />
          )}
        </Box>
        {canSwitch && (
          <Box className={classes.controls}>
            <Box className={classes.navigation}>
              <button
                type="button"
                className={classes.navButton}
                aria-label="Previous file"
                onClick={() => goToOffset(-1)}
              >
                <PreviewChevron direction="left" className={classes.navIcon} />
              </button>
              <button
                type="button"
                className={classes.navButton}
                aria-label="Next file"
                onClick={() => goToOffset(1)}
              >
                <PreviewChevron direction="right" className={classes.navIcon} />
              </button>
            </Box>
            <Box className={classes.pagination}>
              {Array.from({ length: fileCount }, (_, index) => {
                const active = index === fileIndex;
                return (
                  <button
                    key={index}
                    ref={active ? activeBulletRef : undefined}
                    type="button"
                    className={classes.bullet}
                    aria-label={`Show file ${index + 1}`}
                    aria-current={active ? 'true' : undefined}
                    onClick={() => {
                      if (index !== fileIndex) onFileIndexChange(index);
                    }}
                  >
                    <span
                      className={clsx(
                        classes.bulletDot,
                        active && classes.bulletDotActive,
                      )}
                    />
                  </button>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>,
    document.body,
  );
};

PreviewMediaModal.propTypes = {
  name: PropTypes.string,
  url: PropTypes.string.isRequired,
  showVideo: PropTypes.bool,
  fileIndex: PropTypes.number,
  fileCount: PropTypes.number,
  onFileIndexChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

PreviewMediaModal.defaultProps = {
  name: '',
  showVideo: false,
  fileIndex: 0,
  fileCount: 1,
  onFileIndexChange: undefined,
};
